/**
 * Minimal OpenEXR parser for single-channel float32 ZIP/ZIPS compressed files.
 * Designed for depth maps produced by Depth Anything v3 pipeline.
 *
 * Supports: single float32 channel, ZIP (16 lines/block) and ZIPS (1 line/block)
 * compression, and uncompressed. Uses native DecompressionStream API (no deps).
 */

// DecompressionStream is available in modern browsers but not in older TS lib typings
declare const DecompressionStream: {
  new (format: string): { readable: ReadableStream<Uint8Array>; writable: WritableStream<Uint8Array> };
};

export interface ExrResult {
  width: number;
  height: number;
  data: Float32Array;
}

const COMPRESSION_NONE = 0;
const COMPRESSION_ZIPS = 2; // 1 scanline per block
const COMPRESSION_ZIP = 3;  // 16 scanlines per block

function readNullString(view: DataView, offset: number): [string, number] {
  let str = '';
  while (offset < view.byteLength) {
    const ch = view.getUint8(offset++);
    if (ch === 0) break;
    str += String.fromCharCode(ch);
  }
  return [str, offset];
}

async function zlibInflate(compressed: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate');
  const writer = ds.writable.getWriter();
  const reader = ds.readable.getReader();

  writer.write(compressed);
  writer.close();

  const chunks: Uint8Array[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLen = chunks.reduce((s, c) => s + c.length, 0);
  const result = new Uint8Array(totalLen);
  let off = 0;
  for (const c of chunks) {
    result.set(c, off);
    off += c.length;
  }
  return result;
}

/** EXR predictor reconstruction: byte[i] = (byte[i] + byte[i-1] + 128) mod 256 */
function reconstructPredictor(buf: Uint8Array): void {
  for (let i = 1; i < buf.length; i++) {
    buf[i] = (buf[i] + buf[i - 1] + 128) & 0xff;
  }
}

/** EXR interleave reorder: first half → even positions, second half → odd positions */
function reorderInterleave(buf: Uint8Array): Uint8Array {
  const n = buf.length;
  const half = (n + 1) >> 1;
  const out = new Uint8Array(n);
  let t1 = 0, t2 = half, s = 0;
  while (s < n) {
    if (s < n) out[s++] = buf[t1++];
    if (s < n) out[s++] = buf[t2++];
  }
  return out;
}

export async function parseExr(buffer: ArrayBuffer): Promise<ExrResult> {
  const view = new DataView(buffer);
  let offset = 0;

  // 1. Magic number (0x01312F76 little-endian = bytes 76 2F 31 01)
  const magic = view.getUint32(offset, true);
  offset += 4;
  if (magic !== 0x01312f76) {
    throw new Error(`Invalid EXR magic: 0x${magic.toString(16)}`);
  }

  // 2. Version field
  offset += 4; // skip version + flags

  // 3. Parse header attributes
  let compression = -1;
  let xMin = 0, yMin = 0, xMax = 0, yMax = 0;
  let channelBytesPerPixel = 0;

  while (offset < view.byteLength) {
    const [attrName, nameEnd] = readNullString(view, offset);
    offset = nameEnd;
    if (attrName === '') break; // end of header

    const [, typeEnd] = readNullString(view, offset);
    offset = typeEnd;
    const attrSize = view.getUint32(offset, true);
    offset += 4;

    if (attrName === 'compression') {
      compression = view.getUint8(offset);
    } else if (attrName === 'dataWindow') {
      xMin = view.getInt32(offset, true);
      yMin = view.getInt32(offset + 4, true);
      xMax = view.getInt32(offset + 8, true);
      yMax = view.getInt32(offset + 12, true);
    } else if (attrName === 'channels') {
      // Parse channel list to compute bytes per pixel
      let chOff = offset;
      const chEnd = offset + attrSize;
      channelBytesPerPixel = 0;
      while (chOff < chEnd) {
        const [chName, nextOff] = readNullString(view, chOff);
        chOff = nextOff;
        if (chName === '') break;
        const pixelType = view.getInt32(chOff, true); // 0=uint32, 1=half, 2=float
        chOff += 4 + 1 + 3; // pixelType + pLinear + reserved[3]
        chOff += 8; // xSampling(4) + ySampling(4)
        channelBytesPerPixel += pixelType === 1 ? 2 : 4;
      }
    }

    offset += attrSize;
  }

  const width = xMax - xMin + 1;
  const height = yMax - yMin + 1;

  if (compression !== COMPRESSION_ZIP && compression !== COMPRESSION_ZIPS && compression !== COMPRESSION_NONE) {
    throw new Error(`Unsupported EXR compression type: ${compression}`);
  }

  const linesPerBlock = compression === COMPRESSION_ZIP ? 16 : 1;
  const numBlocks = Math.ceil(height / linesPerBlock);

  // 4. Read scanline offset table (uint64 per block, use low 32 bits)
  const offsets: number[] = [];
  for (let i = 0; i < numBlocks; i++) {
    const lo = view.getUint32(offset, true);
    // const hi = view.getUint32(offset + 4, true); // skip high 32 bits
    offset += 8;
    offsets.push(lo);
  }

  // 5. Decompress scanline blocks
  const bytesPerScanline = width * channelBytesPerPixel;
  const outputBuffer = new Uint8Array(height * bytesPerScanline);

  for (let block = 0; block < numBlocks; block++) {
    const blockOff = offsets[block];
    const y = view.getInt32(blockOff, true);
    const dataSize = view.getInt32(blockOff + 4, true);
    const linesInBlock = Math.min(linesPerBlock, height - block * linesPerBlock);
    const expectedBytes = linesInBlock * bytesPerScanline;

    let decompressed: Uint8Array;
    if (compression === COMPRESSION_NONE) {
      decompressed = new Uint8Array(buffer, blockOff + 8, dataSize);
    } else {
      const compressed = new Uint8Array(buffer, blockOff + 8, dataSize);
      decompressed = await zlibInflate(compressed);
      reconstructPredictor(decompressed);
      decompressed = reorderInterleave(decompressed);
    }

    const outY = y - yMin;
    outputBuffer.set(decompressed.subarray(0, expectedBytes), outY * bytesPerScanline);
  }

  // Create Float32Array view over the output
  const float32 = new Float32Array(outputBuffer.buffer, outputBuffer.byteOffset, width * height);
  return { width, height, data: float32 };
}
